import { User } from '../queries/types'
import { AdminProfileDetail } from '../queries/useAdminProfileById'

/**
 * Common profile interface that includes properties from both User and AdminProfileDetail
 */
export interface CommonProfile {
  // Core identity fields
  id?: string
  userId?: string
  profileId?: string
  firstName?: string
  lastName?: string
  emailAddress?: string
  avatarUrl?: string
  color?: string

  // Classification fields
  role?: string
  userRole?: string
  status?: string

  // Contact and personal details
  phone?: string
  about?: string
  publicBio?: string

  // School/Company details
  schoolId?: string
  districtId?: string
  schoolName?: string
  districtName?: string
  companyName?: string
  jobField?: string

  // Preferences and characteristics
  secondaryLanguages?: string[]
  gradeLevels?: string[]
  keywords?: string[]
  contentDescription?: string
  collaboratorDescription?: string
  connectionPreference?: string | string[]
  cities?: string[]

  // Additional administrative info
  createdAt?: string
  approvedAt?: string
  approvedBy?: string
}

/**
 * Determine if a profile is a User from the current user context
 */
export function isUserProfile(profile: any): profile is User {
  return 'userId' in profile && profile.userId !== undefined
}

/**
 * Determine if a profile is an AdminProfileDetail from the admin context
 */
export function isAdminProfile(profile: any): profile is AdminProfileDetail {
  return 'id' in profile && !('userId' in profile) && profile.id !== undefined
}

/**
 * Type that represents either a User or AdminProfileDetail
 */
export type ProfileData = User | AdminProfileDetail

/**
 * Normalize any profile data to a common structure
 * This ensures consistent property access regardless of the source
 */
export function normalizeProfile(
  profileData: ProfileData | null
): CommonProfile | null {
  if (!profileData) return null

  if (isUserProfile(profileData)) {
    // Handle User type
    return {
      id: profileData.profileId || profileData.userId,
      userId: profileData.userId,
      profileId: profileData.profileId || profileData.userId,
      firstName: profileData.firstName,
      lastName: profileData.lastName,
      emailAddress: profileData.emailAddress,
      avatarUrl: profileData.avatarUrl,
      color: profileData.color,
      role: profileData.userRole,
      userRole: profileData.userRole,
      status: profileData.status,
      phone: profileData.phone,
      about: profileData.about,
      publicBio: profileData.publicBio,
      schoolId: profileData.schoolId,
      districtId: profileData.districtId,
      companyName: profileData.companyName,
      jobField: profileData.jobField,
      secondaryLanguages: profileData.secondaryLanguages,
      gradeLevels: profileData.gradeLevels,
      keywords: profileData.keywords,
      contentDescription: profileData.contentDescription,
      collaboratorDescription: profileData.collaboratorDescription,
      connectionPreference: profileData.connectionPreference,
      cities: profileData.cities,
      approvedAt: profileData.approvedAt,
    }
  }

  if (isAdminProfile(profileData)) {
    // Handle AdminProfileDetail type
    return {
      id: profileData.id,
      userId: profileData.id, // Map id to userId for consistency
      profileId: profileData.id,
      firstName: profileData.firstName,
      lastName: profileData.lastName,
      emailAddress: profileData.emailAddress,
      avatarUrl: profileData.avatarUrl,
      color: profileData.color,
      role: profileData.role,
      userRole: profileData.role,
      status: profileData.status,
      about: profileData.about,
      publicBio: profileData.about, // Assume about can be used as publicBio if needed
      schoolName: profileData.schoolName,
      districtName: profileData.districtName,
      companyName: profileData.companyName,
      jobField: profileData.jobField,
      secondaryLanguages: profileData.secondaryLanguages,
      gradeLevels: profileData.gradeLevels,
      connectionPreference: profileData.connectionPreference,
      cities: profileData.cities,
      createdAt: profileData.createdAt,
      approvedAt: profileData.approvedAt,
      approvedBy: profileData.approvedBy,
    }
  }

  // If not a recognized profile type, return a minimal mapping
  return profileData as any
}

/**
 * Get a unified ID from any profile type
 */
export function getProfileId(profile: ProfileData | null): string | undefined {
  if (!profile) return undefined

  if (isUserProfile(profile)) {
    return profile.profileId || profile.userId
  }

  if (isAdminProfile(profile)) {
    return profile.id
  }

  return undefined
}

/**
 * Get user role from any profile type
 */
export function getProfileRole(
  profile: ProfileData | null
): string | undefined {
  if (!profile) return undefined

  if (isUserProfile(profile)) {
    return profile.userRole
  }

  if (isAdminProfile(profile)) {
    return profile.role
  }

  return undefined
}

/**
 * Custom hook to use a normalized profile from potentially different data sources
 */
export function useNormalizedProfile(profileData: ProfileData | null) {
  return normalizeProfile(profileData)
}
